import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";

const HeroSection = () => {
  const parallaxRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!parallaxRef.current) return;

      const parallaxElements =
        parallaxRef.current.querySelectorAll(".parallax");
      const mouseX = e.clientX / window.innerWidth - 0.5;
      const mouseY = e.clientY / window.innerHeight - 0.5;

      parallaxElements.forEach((el) => {
        const speed = parseFloat(el.getAttribute("data-speed"));
        const xPos = mouseX * speed;
        const yPos = mouseY * speed;
        el.style.transform = `translate(${xPos}px, ${yPos}px)`;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <HeroContainer ref={parallaxRef}>
      <HeroBackground>
        <ParallaxGrid className="parallax" data-speed="-20" />
        <ParticlesContainer />
      </HeroBackground>

      <HeroContent>
        <LogoWrapper>
          <motion.img
            src="/images/bmg_logo.png"
            alt="Baltimore Grillz Logo"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="parallax"
            data-speed="30"
          />
        </LogoWrapper>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <GoldSpan>Premium Quality</GoldSpan>
        </motion.h2>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="parallax"
          data-speed="10"
        >
          Baltimore Grillz
        </motion.h1>

        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="parallax"
          data-speed="5"
        >
          & Custom Jewelry
        </motion.h3>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          Elevate your style with our exclusive custom grillz and unique jewelry
          designs. Crafted with precision and passion in Baltimore.
        </motion.p>

        <ButtonsContainer>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <PrimaryButton
              to="/contact"
              whileHover={{
                y: -5,
                boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)",
              }}
            >
              Get in Touch
            </PrimaryButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            <SecondaryButton to="/gallery" whileHover={{ y: -5 }}>
              View Gallery
            </SecondaryButton>
          </motion.div>
        </ButtonsContainer>
      </HeroContent>

      <ScrollDown
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.6 }}
      >
      </ScrollDown>

      <ShapesContainer>
        <FloatingShape
          className="parallax"
          data-speed="40"
          top="15%"
          left="10%"
          size="50px"
          rotate="45deg"
          delay="0"
        />
        <FloatingShape
          className="parallax"
          data-speed="-40"
          top="75%"
          left="85%"
          size="80px"
          rotate="30deg"
          delay="0.3"
        />
        <FloatingShape
          className="parallax"
          data-speed="20"
          top="65%"
          left="15%"
          size="60px"
          rotate="60deg"
          delay="0.5"
        />
        <FloatingShape
          className="parallax"
          data-speed="-25"
          top="20%"
          left="80%"
          size="40px"
          rotate="15deg"
          delay="0.7"
        />
      </ShapesContainer>

      <GlowEffect className="parallax" data-speed="50" />
    </HeroContainer>
  );
};

// Keyframes
const pulseGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.6));
  }
  50% {
    filter: drop-shadow(0 0 25px rgba(255, 215, 0, 0.9));
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

// Styled Components
const HeroContainer = styled.section`
  height: 100vh;
  min-height: 700px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding-top: 60px;
  background-color: var(--darker-bg);
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const ParallaxGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(
      rgba(255, 215, 0, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 215, 0, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  perspective: 1000px;
  transform-style: preserve-3d;
  opacity: 0.4;
  z-index: 1;
`;

const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: radial-gradient(
      circle at center,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.8) 100%
    );
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1000px;
  text-align: center;
  padding: 0 var(--spacing-md);

  h1 {
    font-size: 6rem;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;
    line-height: 1;
    text-transform: uppercase;
    font-weight: 800;
    letter-spacing: -1px;
    text-shadow: var(--gold-glow);
    position: relative;

    @media (max-width: 992px) {
      font-size: 4rem;
    }

    @media (max-width: 576px) {
      font-size: 3rem;
    }
  }

  h2 {
    text-transform: uppercase;
    letter-spacing: 8px;
    font-size: 1rem;
    margin-bottom: 1rem;
    font-weight: 400;

    @media (max-width: 576px) {
      font-size: 0.9rem;
      letter-spacing: 4px;
    }
  }

  h3 {
    font-size: 3rem;
    color: white;
    font-weight: 700;
    margin-bottom: 1.5rem;

    @media (max-width: 992px) {
      font-size: 2rem;
    }

    @media (max-width: 576px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 1.2rem;
    max-width: 650px;
    margin: 0 auto 2.5rem;
    color: #d0d0d0;

    @media (max-width: 576px) {
      font-size: 1rem;
    }
  }
`;

const LogoWrapper = styled.div`
  margin-bottom: 2rem;

  img {
    max-width: 200px;
    height: auto;
    animation: ${pulseGlow} 3s ease-in-out infinite;

    @media (max-width: 576px) {
      max-width: 150px;
    }
  }
`;

const GoldSpan = styled.span`
  color: var(--gold);
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const PrimaryButton = styled(motion(Link))`
  background: var(--gold-gradient);
  background-size: 200% 100%;
  color: var(--black);
  padding: 16px 40px;
  font-weight: 600;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  position: relative;
  overflow: hidden;
  z-index: 1;
  box-shadow: var(--gold-glow);
  display: inline-block;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover {
    animation: ${shimmer} 1.5s linear infinite;
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.5);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    transition: 0.5s ease;
    z-index: -1;
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 576px) {
    padding: 14px 30px;
    font-size: 0.9rem;
    width: 100%;
    text-align: center;
  }
`;

const SecondaryButton = styled(motion(Link))`
  background: transparent;
  color: var(--gold);
  border: 1px solid var(--gold);
  padding: 16px 40px;
  font-weight: 600;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  transition: var(--transition-medium);
  display: inline-block;
  font-size: 1rem;

  &:hover {
    background: rgba(255, 215, 0, 0.1);
  }

  @media (max-width: 576px) {
    padding: 14px 30px;
    font-size: 0.9rem;
    width: 100%;
    text-align: center;
  }
`;

const ScrollDown = styled(motion.a)`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--gold);
  text-decoration: none;
  font-size: 0.8rem;
  letter-spacing: 2px;
  z-index: 3;

  span {
    margin-bottom: 10px;
  }

  svg {
    animation: bounce 2s infinite;
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const ShapesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
`;

const FloatingShape = styled.div`
  position: absolute;
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  width: ${(props) => props.size};
  height: ${(props) => props.size};
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.2);
  transform: rotate(${(props) => props.rotate});
  z-index: 2;
  animation: float 10s infinite ease-in-out ${(props) => props.delay}s;

  @keyframes float {
    0%,
    100% {
      transform: translateY(0) rotate(${(props) => props.rotate});
    }
    50% {
      transform: translateY(-20px)
        rotate(${(props) => parseInt(props.rotate) + 5}deg);
    }
  }
`;

const GlowEffect = styled.div`
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(
    circle,
    rgba(255, 215, 0, 0.1) 0%,
    transparent 70%
  );
  z-index: 1;

  @media (max-width: 992px) {
    width: 400px;
    height: 400px;
  }
`;

export default HeroSection;
